import {gsap} from "gsap/all";
import {SOUNDS} from "../Sounds";

// Base class for one gameplay action. The Game creates one per sequence entry,
// mounts it in the action container and waits for onActionComplete. Subclasses
// override start and destroy, and report their result through points/maxPoints.
export class Action {

  constructor(game, config) {
    this.game = game;
    this.app = game.app;
    this.config = config;

    // Where the action builds its visuals (the .action element).
    this.$host = game.$action;

    this.points = 0;
    this.maxPoints = 1;
    this.completed = false;
    this.gaugeEndApplied = false;

    // The last press sound, and how long the step sound waits after it so the
    // two do not start on the same beat, see playStepSound.
    this.inputSound = null;
    this.stepSoundDelaySec = 0.25;
  }

  // Builds the DOM in the host. The game calls this before fading the action
  // content in, so the visuals are already there during the fade.
  build() {
  }

  // Called right after build, while the content is still fading in. Lets an
  // action take input immediately when the wait for start would feel like an
  // unresponsive console.
  prestart() {
  }

  // Starts the gameplay, once the content fade in is done.
  start() {
  }

  // A console button was pressed while this action is the one mounted, from
  // build until the game clears it. The actions playing on the joystick leave
  // it alone.
  onButtonPress(action) {
  }

  // Moves the ground distance gauge to one of the states of the JSON. Every
  // field of a state is optional, so a state naming only a percent leaves the
  // ship, the phase and the altitude alone.
  //
  // The travel time is the duration of the state itself when it names one, then
  // whatever the caller asks for, then the game default. That way the JSON
  // always has the last word on a state that needs its own pace.
  setGaugeState(state, duration) {
    if (!state) return;

    let travelSec = this.game.groundDistanceSec;
    if (duration !== undefined) travelSec = duration;
    if (state.duration !== undefined) travelSec = state.duration;

    this.app.setGaugeState(state, travelSec);
  }

  // Where the gauge sits when the action starts. Optional, an action without
  // one leaves the gauge where the previous action left it.
  applyGaugeStart(duration) {
    this.setGaugeState(this.config.gauge_at_sequence_start, duration);
  }

  // Where the gauge sits once the action is done, whatever the verdict, since
  // the ship keeps falling either way. Optional too. The actions travelling to
  // it over their own duration call this themselves at start, the flag keeps
  // the game from moving it a second time at the end.
  applyGaugeEnd(duration) {
    if (this.gaugeEndApplied) return;
    this.gaugeEndApplied = true;

    const state = this.config.gauge_at_sequence_end;

    // A state marked during_transition belongs to the animation that plays
    // between this action and the next one, so that animation moves it instead
    // of it jumping ahead of it here. See EDL.applyTransitionGauge.
    if (state && state.during_transition) return;

    this.setGaugeState(state, duration);
  }

  // Ends the action and reports back to the game. Safe against double calls.
  complete() {
    if (this.completed) return;
    this.completed = true;
    this.game.onActionComplete(this);
  }

  // Kills everything the action owns (tweens, timers, listeners). Called by the
  // game once the action is done, and on a hard stop or reset.
  destroy() {
  }

  // Shows the action's end verdict: the colored label (label_success or
  // label_error from the config), the matching container glow and the step
  // sound. The game clears both visuals when swapping to the next action.
  showVerdict(success) {
    this.playStepSound(success);
    this.app.onGameActionFinished(this);

    this.game.setActionLabel(
      success ? this.config.label_success : this.config.label_error,
      success ? "success" : "error"
    );
    this.game.setActionGlow(success ? "success" : "error");
  }

  // The sound of a press, right or wrong.
  playInputSound(success) {
    this.inputSound = this.app.audio.play(success ? SOUNDS.rightInput : SOUNDS.wrongInput, this.app.getSoundVolume("buttons"), true);
  }

  // The sound of the verdict. When the press that ended the action is still
  // sounding, it comes in a beat after it rather than right on top of it.
  playStepSound(success) {
    const file = success ? SOUNDS.goodStep : SOUNDS.wrongStep;
    const inputPlaying = this.inputSound && this.app.audio.getTrack(this.inputSound.id);

    const playStep = () => this.app.audio.play(file, this.app.getSoundVolume("buttons"), true);

    // Played straight away when there is nothing to wait for. The joystick
    // actions reach here from inside their ticker callback, where a zero delay
    // call never went off.
    if (inputPlaying) {
      gsap.delayedCall(this.stepSoundDelaySec, playStep);
    } else {
      playStep();
    }
  }

  // Finds a button definition from the action config by its id.
  buttonById(id) {
    return (this.config.buttons || []).find((button) => button.id === id) || null;
  }
}
